import FormHeader from "@/components/FormHeader"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { listJenisPortofolio } from "@/constants"
import api from "@/lib/axios"
import { JenisPortofolio, Option, Periode, SysBerkasPortofolio } from "@/types"
import { AxiosError } from "axios"
import { Loader2 } from "lucide-react"
import { useState } from "react"
import toast from "react-hot-toast"
import { FaPlus } from "react-icons/fa6"
import { MdModeEdit } from "react-icons/md"
import { RiDeleteBack2Line } from "react-icons/ri"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useSearchParams } from "react-router-dom"
// import { MultiSelect } from "@/components/ui/multi-select";

const postData = async (newData: SysBerkasPortofolio) => {
  const response = await api.post("/setting/sys-berkas/store", newData)
  return response.data
}

const patchData = async (newData: SysBerkasPortofolio) => {
  const response = await api.patch("/setting/sys-berkas/update", newData)
  return response.data
}

const deleteData = async (deletedData: {
  id_sys_berkas_portofolio: string
}) => {
  const response = await api({
    url: "/setting/sys-berkas/delete",
    method: "DELETE",
    data: deletedData,
  })
  return response.data
}

const listMaxFileSize = [256, 512, 1024, 2048, 5120]
const listExtension = [
  "pdf", // PDF files
  "doc", // Microsoft Word (older versions)
  "docx", // Microsoft Word (modern versions)
  "xls", // Microsoft Excel (older versions)
  "xlsx", // Microsoft Excel (modern versions)
  "jpeg", // JPEG image
  "jpg", // JPG image (alternative extension)
  "png", // PNG image
  "gif", // GIF image
]

export default function FormSysBerkasPortofolio({
  method,
  selectedRow,
  closeModal,
}: {
  method: "POST" | "PATCH" | "DELETE"
  selectedRow?: Periode | null

  closeModal: () => void
}) {
  const isPost = method === "POST"
  const isPatch = method === "PATCH"
  const isDelete = method === "DELETE"

  const [searchParams] = useSearchParams()
  const id_sys_periode_params = searchParams.get("id")

  const [form, setForm] = useState<SysBerkasPortofolio>({
    id_sys_berkas_portofolio: "",
    bidang: null,
    nama_jenis_berkas: "",
    slug_jenis_berkas: "",
    keterangan_berkas: "",
    extension_allowed: [],
    max_upload: 0,
    is_active: false,
  })

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listPeriode"] })
      queryClient.invalidateQueries({
        queryKey: ["periode", id_sys_periode_params],
      })
      closeModal()
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    mutate(form)
  }

  if (isDelete)
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<RiDeleteBack2Line />}
          title="Delete Data Setting Berkas"
          description={`Apakah anda yakin akan menghapus data?`}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button
            variant={"destructive"}
            className="flex items-center gap-3"
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Hapus"
            )}
          </Button>
        </div>
      </form>
    )

  return (
    <form onSubmit={handleSubmit}>
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={
          isPost
            ? "Insert Data List Berkas Portofolio"
            : "Update Data List Berkas Portofolio"
        }
      />

      <section className="flex flex-col gap-y-3">
        <div>
          <Label>Bidang</Label>
          <Select
            value={form.bidang || ""}
            onValueChange={(value: string) => {
              const bidang = value as JenisPortofolio
              setForm((prev) => ({
                ...prev,
                bidang,
              }))
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder={"Pilih Bidang"}>
                {form.bidang &&
                  listJenisPortofolio?.find(({ value }) => value == form.bidang)
                    ?.label}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {listJenisPortofolio?.map((option: Option, index: number) => (
                <SelectItem key={index} value={`${option.value}`}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Nama Jenis Berkas</Label>
          <Input
            placeholder="Ketik nama jenis berkas..."
            value={form.nama_jenis_berkas}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                nama_jenis_berkas: event.target.value,
              }))
            }
          />
        </div>
        <div>
          <Label>Max File Size</Label>
          <div className="mt-1 flex gap-3">
            {listMaxFileSize.map((size) => {
              return (
                <div key={size} className="flex items-center gap-3">
                  <Checkbox
                    checked={form.max_upload === size}
                    onCheckedChange={() =>
                      setForm((prev) => ({ ...prev, max_upload: size }))
                    }
                  />
                  <Label>{size} KB</Label>
                </div>
              )
            })}
          </div>
        </div>
        <div className="mb-3">
          <Label>Extension Allowed</Label>
          <div className="mt-1 flex gap-3">
            {listExtension.map((extension) => {
              const isChecked = form.extension_allowed?.includes(extension)

              return (
                <div key={extension} className="flex items-center gap-3">
                  <Checkbox
                    checked={isChecked}
                    onCheckedChange={() =>
                      setForm((prev) => ({
                        ...prev,
                        extension_allowed: isChecked
                          ? prev.extension_allowed?.filter(
                              (ext) => ext !== extension
                            ) || []
                          : [...(prev.extension_allowed || []), extension],
                      }))
                    }
                  />
                  <Label>{extension}</Label>
                </div>
              )
            })}
          </div>
        </div>

        <div>
          <div className="flex gap-3">
            <Switch
              id="is_active"
              checked={form.is_active}
              onCheckedChange={() =>
                setForm((prev) => ({
                  ...prev,
                  is_active: !prev.is_active,
                }))
              }
            />
            <Label htmlFor="is_active">Status (nonaktif/aktif)</Label>
          </div>
        </div>

        <div className="mt-3 flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button className="flex items-center gap-3" disabled={isPending}>
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Simpan"
            )}
          </Button>
        </div>

        {selectedRow?.sys_berkas && (
          <div className="mt-3 border rounded-xl p-3">
            <h1 className="font-semibold">Berkas yang sudah di tambahkan.</h1>
            <ul>
              {selectedRow?.sys_berkas.map((item, index) => {
                return (
                  <li key={index}>- {item.jenis_berkas?.nama_jenis_berkas}</li>
                )
              })}
            </ul>
          </div>
        )}
      </section>
    </form>
  )
}
